// Daily readings in three traditions: today's sky read against your own chart.
// Deterministic (astronomy-engine and calendar arithmetic, never a language
// model). Checked by scripts/astro/verify_daily_lenses.mjs.
//
//   Vedic:   today's Moon nakshatra, counted from your birth Moon's nakshatra
//            (Tara Bala, one of the counts Jyotish uses to judge a day).
//   Maya:    today's Cholq'ij day (number and day sign) and where it sits
//            against your own day sign in the 260-day count.
//   Chinese: today's day animal from the sexagenary day cycle, against your
//            year animal (the almanac's harmonies, clashes and harms).
//
// "Today" is the civil date in the reader's time zone. The Moon's nakshatra is
// taken at the exact moment asked for, with the time it moves on.

import { birthDataToUtc, bodyLongitude, localToUtc, mod360 } from "./core.js";
import { calculateMayanChart } from "./mayan.js";
import { calculateChineseChart } from "./chinese.js";
import { CHINESE_ANIMALS, CHINESE_ELEMENTS, MAYAN_DAY_SIGNS } from "../../components/star-charts/constants.js";

const DAY_MS = 86400000;
const orList = (xs) => (xs.length < 3 ? xs.join(" or ") : `${xs.slice(0, -1).join(", ")}, or ${xs[xs.length - 1]}`);
const cap = (t) => t[0].toUpperCase() + t.slice(1);
const aAn = (word) => `${/^[AEIOU]/i.test(word) ? "an" : "a"} ${word}`;

/* ─────────────────────────── Vedic ─────────────────────────── */

// Lahiri ayanamsa, the same linear formula vedicMath.js uses, so the birth
// nakshatra here matches the one the Vedic chart screen shows.
export function lahiriAyanamsa(date) {
  const T = (date.getTime() / DAY_MS + 2440587.5 - 2451545.0) / 36525;
  return 23.85694444 + 1.3955 * T;
}

const NAK_WIDTH = 360 / 27;

// Names and rulers match vedicMath.js. `symbol` is the nakshatra's classical
// symbol (shown where the tile opens); `line` says what it's about in one
// plain sentence, for the tile itself and Today's card.
export const NAKSHATRAS = [
  { name: "Ashwini", ruler: "ketu", symbol: "the horse's head",
    line: "This mansion belongs to the healer twins and favors quick, fresh starts." },
  { name: "Bharani", ruler: "venus", symbol: "the womb",
    line: "This mansion carries something heavy until it's ready to be born." },
  { name: "Krittika", ruler: "sun", symbol: "the blade and the flame",
    line: "This mansion burns and cuts, clearing away what isn't needed." },
  { name: "Rohini", ruler: "moon", symbol: "the cart",
    line: "This is the mansion of growth, beauty and the comforts that make life good." },
  { name: "Mrigashira", ruler: "mars", symbol: "the deer's head",
    line: "This mansion is the searcher, curious and following the next scent." },
  { name: "Ardra", ruler: "rahu", symbol: "the teardrop",
    line: "This mansion brings storms, and the air is clearer once they pass." },
  { name: "Punarvasu", ruler: "jupiter", symbol: "the quiver of arrows",
    line: "This mansion is about coming back, to the light and to home." },
  { name: "Pushya", ruler: "saturn", symbol: "the cow's udder",
    line: "This mansion nourishes, and Jyotish counts it among the kindest of the 27." },
  { name: "Ashlesha", ruler: "mercury", symbol: "the coiled serpent",
    line: "This mansion holds on tight, with a sharp mind and a watchful eye." },
  { name: "Magha", ruler: "ketu", symbol: "the throne",
    line: "This mansion honors the ancestors and the dignity you inherit from them." },
  { name: "Purva Phalguni", ruler: "venus", symbol: "the front legs of the bed",
    line: "This mansion is for rest, pleasure and enjoying what you've earned." },
  { name: "Uttara Phalguni", ruler: "sun", symbol: "the back legs of the bed",
    line: "This mansion keeps its word and helps the people who count on it." },
  { name: "Hasta", ruler: "moon", symbol: "the hand",
    line: "This mansion works with its hands, good for craft and getting things done." },
  { name: "Chitra", ruler: "mars", symbol: "the bright jewel",
    line: "This mansion is the maker, drawn to building things that are beautiful." },
  { name: "Swati", ruler: "rahu", symbol: "the young shoot in the wind",
    line: "This mansion moves like the wind, independent and hard to pin down." },
  { name: "Vishakha", ruler: "jupiter", symbol: "the archway",
    line: "This mansion has a goal in sight and keeps going until it gets there." },
  { name: "Anuradha", ruler: "saturn", symbol: "the lotus",
    line: "This mansion is friendship and devotion, loyal through hard times." },
  { name: "Jyeshtha", ruler: "mercury", symbol: "the earring",
    line: "This is the mansion of the eldest, who takes charge and protects others." },
  { name: "Mula", ruler: "ketu", symbol: "the tied roots",
    line: "This mansion pulls things up by the roots to see what's really underneath." },
  { name: "Purva Ashadha", ruler: "venus", symbol: "the winnowing fan",
    line: "This mansion wins early and is hard to stop once it gets going." },
  { name: "Uttara Ashadha", ruler: "sun", symbol: "the elephant's tusk",
    line: "This mansion's wins are final, and it keeps what it wins." },
  { name: "Shravana", ruler: "moon", symbol: "the ear",
    line: "This mansion is the listener, and it learns by paying attention." },
  { name: "Dhanishta", ruler: "mars", symbol: "the drum",
    line: "This mansion keeps the beat, with music, rhythm and plenty to go around." },
  { name: "Shatabhisha", ruler: "rahu", symbol: "the empty circle",
    line: "This mansion is the healer who works alone, private and drawn to cures." },
  { name: "Purva Bhadrapada", ruler: "jupiter", symbol: "the front of the funeral cot",
    line: "This mansion runs hot, and it doesn't give up on what it believes." },
  { name: "Uttara Bhadrapada", ruler: "saturn", symbol: "the back of the funeral cot",
    line: "This mansion is deep water, calm on top and patient underneath." },
  { name: "Revati", ruler: "mercury", symbol: "the fish",
    line: "This mansion guides travelers home safely, and it's the last of the 27." },
];

/** Sidereal (Lahiri) Moon longitude at an instant, degrees. */
export function siderealMoon(date) {
  return mod360(bodyLongitude("moon", date) - lahiriAyanamsa(date));
}

export const nakshatraIndexAt = (date) => Math.floor(siderealMoon(date) / NAK_WIDTH);

/** When the Moon next leaves the nakshatra it's in at `date` (to the minute). */
export function nakshatraEndsAt(date) {
  const idx = nakshatraIndexAt(date);
  let lo = date.getTime();
  let hi = lo;
  // The Moon crosses a 13°20' nakshatra in about a day; step hourly to bracket it.
  for (let h = 0; h < 48; h++) {
    hi = lo + 3600000;
    if (nakshatraIndexAt(new Date(hi)) !== idx) break;
    lo = hi;
  }
  while (hi - lo > 60000) {
    const mid = (lo + hi) / 2;
    if (nakshatraIndexAt(new Date(mid)) === idx) lo = mid; else hi = mid;
  }
  return new Date(hi);
}

// Tara Bala: count from your birth nakshatra (1) to today's, take it in
// nines. Sampat, Kshema, Sadhana, Mitra and Parama Mitra are the kind ones;
// Vipat, Pratyari and Naidhana the hard ones. Most lists put Janma with the
// hard ones too (care for the body); it's marked mixed here on purpose, and
// the copy still keeps big starts off it. Tara Bala is one muhurta factor,
// not the whole verdict on a day, and the copy says so.
export const TARAS = [
  { n: 1, name: "Janma", meaning: "birth", tone: "mixed",
    line: "The Moon is back in your birth nakshatra. You feel everything a little closer to the skin.",
    reading: "Janma tara, the Moon on your own birth star. Feelings run near the surface and the body asks for care. Jyotish saves big launches for another day and uses this one to check in with yourself." },
  { n: 2, name: "Sampat", meaning: "wealth", tone: "kind",
    line: "A Sampat day for you: good for money, trades and anything you want to see grow.",
    reading: "Sampat tara, the star of wealth. By Tara Bala it's a good day for earning, buying, asking for a raise or putting effort into something you want to see grow." },
  { n: 3, name: "Vipat", meaning: "danger", tone: "hard",
    line: "A Vipat day for you. Small things go sideways, so leave the big starts for later.",
    reading: "Vipat tara, the star of danger. Nothing dramatic, more the day the bus is late and the file won't save. Tradition says finish what's already moving and start the important things on a kinder day." },
  { n: 4, name: "Kshema", meaning: "well-being", tone: "kind",
    line: "A Kshema day for you: steady, safe and good for looking after yourself and home.",
    reading: "Kshema tara, the star of well-being. A settled day. The count favors it for home, health, rest and anything that keeps you safe." },
  { n: 5, name: "Pratyari", meaning: "obstacle", tone: "hard",
    line: "A Pratyari day for you. Expect pushback, and pick the fights worth having.",
    reading: "Pratyari tara, the star of obstacles. People and plans push back more than usual. Tradition reads it as a day to go around a wall, not through it." },
  { n: 6, name: "Sadhana", meaning: "achievement", tone: "kind",
    line: "A Sadhana day for you: effort pays off. Get the hard thing done.",
    reading: "Sadhana tara, the star of achievement. Practice and effort land today. Days like this are picked to begin study, training or a project that needs grit." },
  { n: 7, name: "Naidhana", meaning: "destruction", tone: "hard",
    line: "A Naidhana day, the hardest of the nine taras. Keep it simple and save the big moves.",
    reading: "Naidhana tara, the hardest of the nine. Tradition says don't sign, launch or travel far on it if you can help it. Keep the day plain and kind to yourself." },
  { n: 8, name: "Mitra", meaning: "friend", tone: "kind",
    line: "A Mitra day for you: friendly. Help comes more easily when you ask.",
    reading: "Mitra tara, the star of a friend. People are easier to reach and quicker to help. A good day to ask, meet or mend." },
  { n: 9, name: "Parama Mitra", meaning: "best friend", tone: "kind",
    line: "A Parama Mitra day, the kindest of the nine taras. Ask for what you want.",
    reading: "Parama Mitra tara, the star of the best friend, the kindest of the nine. Jyotish treats it as the day for whatever you most want to go well." },
];

// A tara's name in a line, so screens can link it to the glossary's tara entry.
export const TARA_NAME = /\b(?:Parama Mitra|Anujanma|Trijanma|Janma|Sampat|Vipat|Kshema|Pratyari|Sadhana|Naidhana|Mitra)\b/;

// Muhurta texts read the hard taras (and Janma) most strongly in the first
// round of nine and more lightly in the second and third.
const ORDINAL_ROUND = { 2: "second", 3: "third" };
const LATER_ROUND = {
  hard: (round) => ` This falls in your ${ORDINAL_ROUND[round]} round of nine, and tradition reads it more lightly there.`,
};
// Janma in rounds 2 and 3: the 10th and 19th from the birth star.
const echoReading = (round) => `${round === 2 ? "Anujanma" : "Trijanma"}, the ${round === 2 ? "10th" : "19th"} from your birth star, an echo of Janma. Feelings sit near the surface, and tradition reads it more lightly than the birth star itself.`;

/** Tara for a birth nakshatra index and today's index (both 0-26). */
export function taraOf(birthIdx, todayIdx) {
  const count = ((todayIdx - birthIdx + 27) % 27) + 1; // 1..27, birth star counts as 1
  return { count, round: Math.ceil(count / 9), tara: TARAS[(count - 1) % 9] };
}

/** Every nakshatra the Moon passed through on the birth day when the time is unknown. */
export function birthNakshatras(birthData) {
  const tz = birthData.birthLocation?.timezone;
  if (birthData.hasBirthTime && tz) return [nakshatraIndexAt(birthDataToUtc(birthData))];
  const { year, month, day } = birthData.birthDate;
  if (!tz) {
    // No time zone: the birth day could be anywhere from UTC-12 to UTC+14.
    const a = nakshatraIndexAt(new Date(Date.UTC(year, month - 1, day) - 14 * 3600000));
    const b = nakshatraIndexAt(new Date(Date.UTC(year, month - 1, day + 1) + 12 * 3600000));
    const out = [a];
    for (let i = a; i !== b; ) { i = (i + 1) % 27; out.push(i); }
    return out;
  }
  const start = localToUtc({ year, month, day, hour: 0, minute: 0 }, tz);
  const next = new Date(Date.UTC(year, month - 1, day + 1));
  const end = new Date(localToUtc({ year: next.getUTCFullYear(), month: next.getUTCMonth() + 1, day: next.getUTCDate(), hour: 0, minute: 0 }, tz) - 1000);
  const a = nakshatraIndexAt(start), b = nakshatraIndexAt(end);
  const out = [a];
  for (let i = a; i !== b; ) { i = (i + 1) % 27; out.push(i); }
  return out;
}

function vedicLens(birthData, date, timeZone) {
  const todayIdx = nakshatraIndexAt(date);
  const today = NAKSHATRAS[todayIdx];
  const endsAt = nakshatraEndsAt(date);
  const until = new Intl.DateTimeFormat("en-US", { timeZone, hour: "numeric", minute: "2-digit", weekday: "long" }).format(endsAt);
  const options = birthNakshatras(birthData);
  const base = {
    today: { ...today, index: todayIdx },
    endsAt,
    birthOptions: options.map((i) => NAKSHATRAS[i].name),
  };
  const where = `The Moon is in ${today.name} until ${until}. ${today.line}`;
  if (options.length !== 1) {
    // Without a birth time the birth Moon could sit in two nakshatras, and
    // the count from each gives a different tara, so make no claim.
    return {
      ...base, birth: null, tara: null, taraLabel: null, count: null, round: null,
      line: `The Moon is in ${today.name}. ${today.line}`,
      reading: `${where} Your birth Moon was in ${orList(options.map((i) => NAKSHATRAS[i].name))}, depending on the hour you were born, so your count for today needs your birth time and place.`,
    };
  }
  const birthIdx = options[0];
  const { count, round, tara } = taraOf(birthIdx, todayIdx);
  const later = round > 1;
  const line = tara.n === 1 && later
    ? "An echo of your birth star in the count. Feelings sit a little closer to the skin."
    : tara.tone === "hard" && later
      ? `A ${tara.name} day, but in a later round of your count, so a lighter one. Keep big moves simple.`
      : tara.line;
  const body = tara.n === 1 && later ? echoReading(round) : tara.reading + (tara.tone === "hard" && later ? LATER_ROUND.hard(round) : "");
  return {
    ...base,
    birth: { ...NAKSHATRAS[birthIdx], index: birthIdx },
    tara, count, round,
    // What to call today's tara on screen: the 10th and 19th are Anujanma and Trijanma.
    taraLabel: tara.n === 1 && later ? (round === 2 ? "Anujanma" : "Trijanma") : tara.name,
    line,
    reading: `${where} Counted from your birth Moon in ${NAKSHATRAS[birthIdx].name}, that's number ${count}. ${body}`,
  };
}

/* ─────────────────────────── Maya ─────────────────────────── */

// What K'iche' daykeepers use each day for, in the order of MAYAN_DAY_SIGNS.
const MAYA_DAY = {
  imix: "Imox is water and the deep mind. Dreams run vivid and thinking can get muddy. Daykeepers ask for a clear head on it.",
  ik: "Iq' is wind and breath. Tempers can blow in fast. It's a day to clear the air, in the room and in yourself.",
  akbal: "Aq'ab'al is the dawn. Daykeepers ask for new beginnings on it, and things kept in the dark tend to come out.",
  kan: "K'at is the net. Things get tangled or caught. A day to gather what's yours and untie a knot or two.",
  chikchan: "Kan is the serpent, power moving through the body. A strong day for justice and for saying what's true.",
  kimi: "Kame is the day of the ancestors. People remember their dead on it and ask for peace. Let something end well.",
  manik: "Kej is the deer, the one who holds up a corner of the world. A day for standing firm and leading.",
  lamat: "Q'anil is the seed. Daykeepers plant on it, in the field and otherwise. Start what you want to see grow.",
  muluk: "Toj is the offering, the day for paying what's owed. Settle a debt, give thanks, return the favor.",
  ok: "Tz'i' is the dog, loyalty and the law. A day for fairness and for keeping your word.",
  chuwen: "B'atz' is the thread. The count's weavers and artists honor it. A day to make things and tie loose ends together.",
  eb: "E is the road. A good day to set out, travel, or take the next step on the path you're on.",
  ben: "Aj is the cane, the house and the family in it. A day for home and the people under its roof.",
  ix: "I'x is the jaguar and the earth. Daykeepers honor the land on it. A day of quiet, hidden strength.",
  men: "Tz'ikin is the bird, the messenger. People ask for luck and good fortune on it. Keep an eye out for signs.",
  kib: "Ajmaq is the day of forgiveness. Ask for it, give it, own a mistake. Daykeepers count it the day faults are let go.",
  kaban: "No'j is thought and good counsel. A clear day for ideas, decisions and asking someone wise.",
  etznab: "Tijax is the flint blade. It cuts both ways: hard truths, quick conflict, and the kind of cut that heals.",
  kawak: "Kawoq is the storm and the gathering of people. Family and community matter today, rough weather included.",
  ajpu: "Ajpu is the hunter and the sun, the day of the Hero Twins. Courage and skill find their mark.",
};

const BAND = { gentle: "a gentle number, so it comes through softly", middle: "a middle number, so it comes through balanced", strong: "a strong number, so it comes through at full force" };

function mayaLens(birthData, civil) {
  const today = calculateMayanChart({ birthDate: civil });
  const yours = calculateMayanChart(birthData);
  const sign = today.daySign, tone = today.tone;
  const sameSign = today.daySignIdx === yours.daySignIdx;
  const birthday = sameSign && today.toneIdx === yours.toneIdx;
  const daysToYourSign = (yours.daySignIdx - today.daySignIdx + 20) % 20; // 0 = today
  // Day of your own 260-day round: day 1 is your birthday in the count.
  const civilMs = Date.UTC(civil.year, civil.month - 1, civil.day);
  const birthMs = Date.UTC(birthData.birthDate.year, birthData.birthDate.month - 1, birthData.birthDate.day);
  const dayOfRound = ((Math.round((civilMs - birthMs) / DAY_MS) % 260) + 260) % 260 + 1;

  const name = `${tone.number} ${sign.name}`;
  let relation, line;
  if (birthday) {
    relation = "birthday";
    line = `Today is ${name}, your birthday in the 260-day count. A whole round since the last one.`;
  } else if (sameSign) {
    relation = "your-sign";
    line = `Today is ${name}, ${aAn(yours.daySign.name)} day, and that's your sign. A day to ask for what it gives.`;
  } else {
    relation = "other";
    line = `Today is ${name}. Your own sign, ${yours.daySign.name}, comes round in ${daysToYourSign} ${daysToYourSign === 1 ? "day" : "days"}.`;
  }
  const yoursLine = birthday
    ? "In K'iche' communities people mark this day: it comes once every 260 days."
    : sameSign
      ? `Your own sign comes round every 20 days, and daykeepers treat it as your day to give thanks and ask.`
      : `You're on day ${dayOfRound} of your own 260-day round.`;
  return {
    today: { daySign: sign, tone, daySignIdx: today.daySignIdx, toneIdx: today.toneIdx },
    yours: { daySign: yours.daySign, tone: yours.tone },
    relation, daysToYourSign, dayOfRound,
    line,
    reading: `${MAYA_DAY[sign.key]} Its number is ${tone.number}, ${BAND[tone.strength]}. ${yoursLine}`,
  };
}

/* ─────────────────────────── Chinese ─────────────────────────── */

const STEMS = [
  { pinyin: "Jia", char: "甲" }, { pinyin: "Yi", char: "乙" }, { pinyin: "Bing", char: "丙" }, { pinyin: "Ding", char: "丁" },
  { pinyin: "Wu", char: "戊" }, { pinyin: "Ji", char: "己" }, { pinyin: "Geng", char: "庚" }, { pinyin: "Xin", char: "辛" },
  { pinyin: "Ren", char: "壬" }, { pinyin: "Gui", char: "癸" },
];
const BRANCH_CHARS = ["子", "丑", "寅", "卯", "辰", "巳", "午", "未", "申", "酉", "戌", "亥"];
const BRANCH_PINYIN = ["Zi", "Chou", "Yin", "Mao", "Chen", "Si", "Wu", "Wei", "Shen", "You", "Xu", "Hai"];

/**
 * Day pillar for a civil date. Index 0 is Jia Zi. The Julian Day Number of
 * the date (at noon) plus 49, mod 60, gives the cycle position: 1949-10-01
 * was a Jia Zi day and 2000-01-01 a Wu Wu day. The day changes at midnight
 * here, as in the printed almanacs (some BaZi schools turn it at 23:00).
 */
export function dayPillar({ year, month, day }) {
  const jdn = Math.round(Date.UTC(year, month - 1, day, 12) / DAY_MS + 2440587.5);
  const cycle = (((jdn + 49) % 60) + 60) % 60;
  const stem = cycle % 10, branch = cycle % 12;
  return {
    cycle, stem, branch,
    animal: CHINESE_ANIMALS[branch],
    element: CHINESE_ELEMENTS[Math.floor(stem / 2)],
    polarity: stem % 2 === 0 ? "Yang" : "Yin",
    pinyin: `${STEMS[stem].pinyin} ${BRANCH_PINYIN[branch]}`,
    chars: `${STEMS[stem].char}${BRANCH_CHARS[branch]}`,
  };
}

// Branch pairs (0 = Rat). Six harmonies, the trines (three harmonies),
// clashes six apart, and the six harms.
const HARMONY = [1, 0, 11, 10, 9, 8, 7, 6, 5, 4, 3, 2];
const HARM = [7, 6, 5, 4, 3, 2, 1, 0, 11, 10, 9, 8];
// The three punishments (Tiger-Snake-Monkey, Ox-Goat-Dog, Rat-Rabbit) and
// self-punishment for Dragon, Horse, Rooster and Pig on their own days.
const PUNISH = { 0: [3], 3: [0], 2: [5, 8], 5: [2, 8], 8: [2, 5], 1: [7, 10], 7: [1, 10], 10: [1, 7] };
const SELF_PUNISH = new Set([4, 6, 9, 11]);

/** Whether two branches are one of the punishment pairs. */
export const isPunishment = (a, b) => !!PUNISH[a]?.includes(b);

/** How a day branch sits against a year branch. A punishment pair that is
 *  also a clash, harm or harmony keeps that name; see isPunishment. */
export function branchRelation(yearBranch, dayBranch) {
  if (yearBranch === dayBranch) return SELF_PUNISH.has(yearBranch) ? "self-punish" : "same";
  if ((yearBranch - dayBranch + 12) % 12 === 6) return "clash";
  if (HARMONY[yearBranch] === dayBranch) return "harmony";
  if ((yearBranch - dayBranch + 12) % 4 === 0) return "trine";
  if (HARM[yearBranch] === dayBranch) return "harm";
  if (PUNISH[yearBranch]?.includes(dayBranch)) return "punish";
  return "neutral";
}

const CHINESE_LINE = {
  same: (d, y) => `${cap(aAn(d))} day, and you're ${aAn(y)}. The day runs on your own animal's energy.`,
  harmony: (d, y) => `${cap(aAn(d))} day. ${d} and ${y} are one of the six harmonies, so the day is on your side.`,
  trine: (d, y) => `${cap(aAn(d))} day. ${d} and ${y} share a team of three, so help shows up.`,
  clash: (d, y) => `${cap(aAn(d))} day, and ${d} clashes with ${y}. Hold off on the big decisions.`,
  harm: (d, y) => `${cap(aAn(d))} day. ${d} and ${y} rub each other wrong: small misunderstandings.`,
  neutral: (d) => `${cap(aAn(d))} day. No pull either way on your animal, so it's yours to shape.`,
  punish: (d, y) => `${cap(aAn(d))} day. ${d} and ${y} are a punishment pair: a day that tests your patience.`,
  "self-punish": (d) => `${cap(aAn(d))} day, your own animal, and ${aAn(d)} can trip over itself on it. Go easy.`,
};
const RELATION_PHRASE = {
  same: "as your own animal's day", "self-punish": "as your own animal's day, a self-punishment",
  harmony: "as a harmony", trine: "as a team of three", clash: "as a clash", harm: "as a harm",
  punish: "as a punishment", neutral: "as neutral",
};
const CHINESE_READING = {
  same: "In the almanac, a day of your own animal doubles what your animal already does, the good habits and the stubborn ones.",
  harmony: "The six harmonies pair the animals that quietly look out for each other. Good for asking a favor and for working with someone.",
  trine: "The three harmonies group the animals four apart into teams that pull the same way. Good for group work and plans with others.",
  clash: "Every Chinese almanac lists which animal each day clashes with, and today it's yours. Expect things to bump: plans, moods, schedules. The almanac tells people of your animal to keep important matters for another day.",
  harm: "The six harms are the quieter frictions, the pairs that misread each other. Say the thing plainly and double-check messages.",
  punish: "The punishments are pairs that make each other pay for shortcuts. Small rules matter today, and so does your temper.",
  "self-punish": "Dragon, Horse, Rooster and Pig each punish themselves on their own day: too much of their own nature. Watch the habit you know best.",
  neutral: "Today's animal is neither friend nor rival to yours, so the day leans on its element and what you bring to it.",
};

/** The year animal counted from Li Chun (the Sun at 315°, about Feb 4), the
    way almanacs count it; the app counts from Lunar New Year, as the Chart
    does. One animal, or two when the birth falls on Li Chun day and the hour
    (unknown, or without a zone) decides. Also used by the first-run reveal. */
export function liChunAnimals(birthData) {
  const by = birthData.birthDate;
  const tz = birthData.birthLocation?.timezone;
  const liChunAnimalAt = (date) => {
    const sunLon = bodyLongitude("sun", date);
    const y = by.month <= 2 && sunLon >= 270 && sunLon < 315 ? by.year - 1 : by.year;
    return CHINESE_ANIMALS[(((y - 4) % 12) + 12) % 12];
  };
  const ends = birthData.hasBirthTime && tz
    ? [birthDataToUtc(birthData)]
    : tz
      ? [localToUtc({ ...by, hour: 0, minute: 0 }, tz), new Date(localToUtc({ ...by, hour: 23, minute: 59 }, tz).getTime() + 59000)]
      : [new Date(Date.UTC(by.year, by.month - 1, by.day) - 14 * 3600000), new Date(Date.UTC(by.year, by.month - 1, by.day + 1) + 12 * 3600000)];
  return [...new Map(ends.map(liChunAnimalAt).map((a) => [a.key, a])).values()];
}

function chineseLens(birthData, civil) {
  const pillar = dayPillar(civil);
  const yours = calculateChineseChart(birthData);
  const yearBranch = yours.animal.order - 1;
  const relation = branchRelation(yearBranch, pillar.branch);
  const d = pillar.animal.name, y = yours.animal.name;
  const clashAnimal = CHINESE_ANIMALS[(pillar.branch + 6) % 12];
  const alsoPunish = relation !== "punish" && isPunishment(yearBranch, pillar.branch);
  const lcAnimals = liChunAnimals(birthData);
  const tz = birthData.birthLocation?.timezone;
  const liChunAnimal = lcAnimals.length === 1 ? lcAnimals[0] : null;
  let liChunNote = "";
  if (lcAnimals.length > 1) {
    liChunNote = ` You were born on Li Chun day, so for almanacs that count your animal from Li Chun, ${tz ? "the hour decides" : "your birth time and place decide"} whether you're ${aAn(lcAnimals[0].name)} or ${aAn(lcAnimals[1].name)}.`;
  } else if (liChunAnimal.key !== yours.animal.key) {
    liChunNote = ` Lunar New Year and Li Chun fell on different sides of your birthday, so almanacs that count from Li Chun would make you ${aAn(liChunAnimal.name)}, and read today ${RELATION_PHRASE[branchRelation(liChunAnimal.order - 1, pillar.branch)]}.`;
  }
  return {
    today: pillar,
    yours: { animal: yours.animal, element: yours.element },
    liChunAnimal: liChunAnimal && liChunAnimal.key !== yours.animal.key ? liChunAnimal : null,
    liChunUncertain: lcAnimals.length > 1,
    relation,
    punishment: relation === "punish" || relation === "self-punish" || alsoPunish,
    clashAnimal,
    line: CHINESE_LINE[relation](d, y),
    reading: `Today is ${aAn(pillar.polarity)} ${pillar.element.name} ${d} day (${pillar.pinyin}, ${pillar.chars}). ${CHINESE_READING[relation]}${alsoPunish ? " The pair is also one of the punishments, so shortcuts cost more than usual." : ""} The almanac says today clashes with the ${clashAnimal.name}.${liChunNote}`,
  };
}

/* ─────────────────────────── entry point ─────────────────────────── */

/** The civil date in a time zone. */
export function civilDate(date, timeZone) {
  const parts = {};
  new Intl.DateTimeFormat("en-US", { timeZone, year: "numeric", month: "numeric", day: "numeric" })
    .formatToParts(date).forEach((p) => { if (p.type !== "literal") parts[p.type] = +p.value; });
  return { year: parts.year, month: parts.month, day: parts.day };
}

/**
 * Today's Vedic, Maya and Chinese readings for a chart.
 * @param birthData the app's birthData shape ({ birthDate, hasBirthTime, birthTime, birthLocation })
 * @param opts.date the moment to read (default now)
 * @param opts.timeZone the reader's IANA zone, which sets "today" (default the device's)
 */
export function dailyLenses(birthData, { date = new Date(), timeZone } = {}) {
  if (!birthData?.birthDate?.year) return null;
  const device = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  let tz = timeZone || device;
  let civil;
  try { civil = civilDate(date, tz); } catch (e) { tz = device; civil = civilDate(date, tz); }
  const safe = (fn) => { try { return fn(); } catch (e) { console.error("dailyLenses:", e); return null; } };
  return {
    date: civil,
    timeZone: tz,
    vedic: safe(() => vedicLens(birthData, date, tz)),
    maya: safe(() => mayaLens(birthData, civil)),
    chinese: safe(() => chineseLens(birthData, civil)),
  };
}
